import React from 'react'
import { ContentBlock, EditorState } from 'draft-js'
import {
    convertFromHTML,
    convertToHTML,
    ExtendedHTMLElement,
    RawDraftContentBlockWithCustomType,
    Tag
} from 'draft-convert'
import styled, { CSSProperties } from 'styled-components'

// styles on editor
export const customBlockStyleFn = (contentBlock: ContentBlock): string => {
    const type = contentBlock.getType()
    switch (type) {
        case 'CENTER':
            return 'ds_textCentered'
        case 'LEFT':
            return 'ds_textOnLeft'
        case 'RIGHT':
            return 'ds_textOnRight'
        case 'JUSTIFY':
            return 'ds_texJustified'
        case 'blockquote':
            return 'ds_blockquote'
    }
}

export const customInlineStyles: { [ksy: string]: CSSProperties } = {
    MONOSPACE: {
        fontFamily: 'monospace'
    },
    H1: {
        display: 'block',
        fontSize: '2em',
        marginTop: '0.67em',
        marginBottom: '0.67em',
        marginLeft: '0',
        marginRight: '0'
    },
    H2: {
        display: 'block',
        fontSize: '1.5em',
        marginTop: '0.83em',
        marginBottom: '0.83em',
        marginLeft: '0',
        marginRight: '0'
    },
    H3: {
        display: 'block',
        fontSize: '1.17em',
        marginTop: '1em',
        marginBottom: '1em',
        marginLeft: '0',
        marginRight: '0'
    },
    H4: {
        display: 'block',
        fontSize: '1em',
        marginTop: '1.33em',
        marginBottom: '1.33em',
        marginLeft: '0',
        marginRight: '0'
    }
}
// styles on editor -- end

export const DSExporterConfig = {
    // eslint-disable-next-line react/display-name
    styleToHTML: (style: string): JSX.Element => {
        switch (style) {
            case 'BOLD':
                return <strong style={{ lineHeight: '16px' }} />
            case 'ITALIC':
                return <i style={{ lineHeight: '16px' }} />
            case 'MONOSPACE':
                return <p style={{ fontFamily: 'monospace', lineHeight: '16px' }} />
            case 'H1':
                return <h1 />
            case 'H2':
                return <h2 />
            case 'h3':
                return <h3 />
            case 'h4':
                return <h4 />
        }
    },
    // eslint-disable-next-line react/display-name
    blockToHTML: (block: RawDraftContentBlockWithCustomType<unknown>): Tag => {
        switch (block.type) {
            case 'CENTER':
                return <div style={{ textAlign: 'center' }} />
            case 'LEFT':
                return <div style={{ textAlign: 'left' }} />
            case 'RIGHT':
                return <div style={{ textAlign: 'right' }} />
            case 'JUSTIFY':
                return <div style={{ textAlign: 'justify' }} />
            case 'blockquote':
                return StyledBlockToHtml
        }
    }
}

const StyledBlockToHtml = styled.blockquote`
    text-align: center;
    background: ${({ theme }) => theme['gray-100']};
    font-family: fantasy;
    border-left: 10px solid ${({ theme }) => theme['gray-400']};
    border-right: 0.1px solid ${({ theme }) => theme['gray-200']};
    border-bottom: 0.1px solid ${({ theme }) => theme['gray-200']};
    border-top: 0.1px solid ${({ theme }) => theme['gray-200']};
    content: open-quote;
    padding: 8px;
    border: 1;
    word-break: break-word;
    max-width: 600px;
`

export const DSImporterConfig = {
    htmlToStyle: (
        nodeName: string,
        node: ExtendedHTMLElement<{ [name: string]: unknown }>,
        currentStyle: Set<string>
    ): Set<string> => {
        switch (nodeName) {
            case 'strong' || 'b':
                return currentStyle.add('BOLD')
            case 'i':
                return currentStyle.add('ITALIC')
            case 'blockquote':
                return currentStyle.add('blockquote')
            case 'p':
                if (node.style.fontFamily === 'monospace') {
                    return currentStyle.add('MONOSPACE')
                }

                return currentStyle
            case 'h1':
                return currentStyle.add('H1')
            case 'h2':
                return currentStyle.add('H2')
            case 'h3':
                return currentStyle.add('H3')
            case 'h4':
                return currentStyle.add('H4')
            default:
                return currentStyle
        }
    },

    htmlToBlock: (
        nodeName: string,
        node: ExtendedHTMLElement<{ [name: string]: unknown }>
    ): { type: string; data: undefined } => {
        // handle alignment items
        if (nodeName === 'div') {
            if (node.style.textAlign) {
                switch (node.style.textAlign) {
                    case 'center':
                        return {
                            type: 'CENTER',
                            data: undefined
                        }
                    case 'left':
                        return {
                            type: 'LEFT',
                            data: undefined
                        }
                    case 'right':
                        return {
                            type: 'RIGHT',
                            data: undefined
                        }
                    case 'justify':
                        return {
                            type: 'JUSTIFY',
                            data: undefined
                        }
                }
            }
        }
    }
}

/*  export const blockRenderMap = DefaultDraftBlockRenderMap.merge(
    Immutable.Map({
        'block-quote': { element: 'div' }
    })
) */

export const htmlImporter = (html: string): EditorState => {
    return EditorState.createWithContent(convertFromHTML(DSImporterConfig)(html))
}

export const htmlExporter = (editorState: EditorState): string => {
    return convertToHTML(DSExporterConfig)(editorState.getCurrentContent())
}
